/**
 * Los nombres de los productos, en el idioma de quien mira.
 *
 * A product's name is the shop's own words, and nothing here translates it. What a product can
 * have is a second name, in English, that a *person* wrote — the owner in the product screen,
 * or whoever wrote the starter catalogue. The English screen shows that one when it exists and
 * the Spanish one when it does not, so an English screen can show Spanish but never shows a
 * guess.
 *
 * `nombre` stays authoritative everywhere that is not a screen: the receipt prints it, the log
 * records it, a sale line keeps the name it was sold under. This only decides what is drawn.
 *
 * Shops that loaded the starter catalogue before products could carry an English name hold
 * those rows without one. They are the catalogue's words, not the shop's, so the catalogue can
 * still vouch for them: a product whose Spanish name is exactly a starter product's gets that
 * product's English. A product somebody renamed stops matching, and shows what they typed.
 */

import { useSyncExternalStore } from 'react';
import { CATALOGO_SEMILLA } from '@abastece/shared';
import { normalizar } from '../lib/texto.js';
import { idiomaActual, suscribirIdioma, type Idioma } from './idioma.js';

/** Anything with a product's names — a `Producto`, or a row that carries one. */
export interface ConNombre {
  nombre: string;
  nombreEn?: string | null;
}

const INGLES_DE_LA_SEMILLA = new Map(
  CATALOGO_SEMILLA.map((p) => [normalizar(p.nombre), p.nombreEn] as const),
);

/** The English this product would show, or null when nobody has written one. */
export function nombreEnIngles(p: ConNombre): string | null {
  const escrito = p.nombreEn?.trim();
  if (escrito) return escrito;
  return INGLES_DE_LA_SEMILLA.get(normalizar(p.nombre)) ?? null;
}

export function nombreProducto(p: ConNombre, idioma: Idioma = idiomaActual()): string {
  return idioma === 'en' ? (nombreEnIngles(p) ?? p.nombre) : p.nombre;
}

/**
 * Every name a product answers to.
 *
 * Search looks at all of them whatever the screen's language, because the person typing does
 * not think about which language the catalogue is in: "rice" and "arroz" are the same shelf.
 */
export function nombresDe(p: ConNombre): string[] {
  const en = nombreEnIngles(p);
  return en && en !== p.nombre ? [p.nombre, en] : [p.nombre];
}

/** One function per language, so the one a component holds changes exactly when the language does. */
const POR_IDIOMA: Record<Idioma, (p: ConNombre) => string> = {
  es: (p) => p.nombre,
  en: (p) => nombreEnIngles(p) ?? p.nombre,
};

/**
 * The display name, subscribed to the language.
 *
 * Returns a function rather than a string for the same reason `useT` does, and a *stable* one
 * per language so it can sit in a `useMemo` dependency list: a list sorted by name re-sorts
 * when the language changes, because its sort key just did.
 */
export function useNombreProducto(): (p: ConNombre) => string {
  const idioma = useSyncExternalStore(suscribirIdioma, idiomaActual, () => 'es' as Idioma);
  return POR_IDIOMA[idioma];
}
